// assets/AllCourses/Exercises/VocabularyBuildingExercise.jsx
"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { StringField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import { cn } from "@/utils/utils";

export default function VocabularyBuildingExercise({
  exercise = {},
  onUpdate,            // Edit mode only
  errors = {},
  isPreview = false,   // false = admin edit, true = student view
  showResults = false,
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise.vocabularyQuestions)
    ? exercise.vocabularyQuestions.map((q) => ({
        word: typeof q.word === "string" ? q.word.trim() : "",
        options: Array.isArray(q.options)
          ? q.options.map((o) => String(o).trim())
          : [],
        correctAnswer:
          typeof q.correctAnswer === "string"
            ? q.correctAnswer.trim()
            : "",
        requireCapitalization: Boolean(q.requireCapitalization),
      }))
    : [];

  /* ================= STUDENT STATE ================= */
  const [state, setState] = useState({
    answers: {}, // { [qIndex]: string }
    hasInteracted: false,
  });

  const completedRef = useRef(false);

  /* ================= RESTORE ATTEMPT ================= */
  useEffect(() => {
    if (!isPreview || !exercise.attempt) return;

    setState({
      answers:
        typeof exercise.attempt.answers === "object"
          ? exercise.attempt.answers
          : {},
      hasInteracted: !!exercise.attempt.hasInteracted,
    });
  }, [isPreview, exercise.attempt]);

  /* ================= RESET ================= */
  useEffect(() => {
    if (isPreview) completedRef.current = false;
  }, [exercise?._id, isPreview]);

  useEffect(() => {
  if (!showResults) completedRef.current = false;
}, [showResults]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= EDIT MODE ================= */
  const updateQuestions = (next) => {
    if (isEditMode) onUpdate("vocabularyQuestions", next);
  };

  const handleQuestionChange = (qIdx, field, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      [field]: typeof value === "string" ? value.trim() : value,
    };
    updateQuestions(next);
  };

  const handleOptionChange = (qIdx, optIdx, value) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx];
    const options = [...q.options];
    options[optIdx] = value.trim();

    next[qIdx] = {
      ...q,
      options,
      correctAnswer: options.includes(q.correctAnswer)
        ? q.correctAnswer
        : "",
    };

    updateQuestions(next);
  };

  const handleAddOption = (qIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      options: [...next[qIdx].options, ""],
    };
    updateQuestions(next);
  };

  const handleRemoveOption = (qIdx, optIdx) => {
    if (!isEditMode) return;
    const next = [...questions];
    const q = next[qIdx];
    const options = q.options.filter((_, i) => i !== optIdx);

    next[qIdx] = {
      ...q,
      options,
      correctAnswer: options.includes(q.correctAnswer)
        ? q.correctAnswer
        : "",
    };

    updateQuestions(next);
  };

  const handleCapitalizationChange = (qIdx, checked) => {
    if (!isEditMode) return;
    const next = [...questions];
    next[qIdx] = {
      ...next[qIdx],
      requireCapitalization: Boolean(checked),
    };
    updateQuestions(next);
  };

  const handleAddQuestion = () => {
    if (!isEditMode) return;
    updateQuestions([
      ...questions,
      {
        word: "",
        options: [],
        correctAnswer: "",
        requireCapitalization: false,
      },
    ]);
  };

  const handleRemoveQuestion = (qIdx) => {
    if (!isEditMode) return;
    updateQuestions(questions.filter((_, i) => i !== qIdx));
  };

  /* ================= STUDENT MODE ================= */
  const handleChange = (qIdx, value) => {
    if (isEditMode || showResults) return;

    setState((prev) => {
      const answers = {
        ...prev.answers,
        [qIdx]: value,
      };

      const next = {
        answers,
        hasInteracted: true,
      };

      setAttempt(next);

      const allAnswered = questions.every(
        (_, i) => answers[i] !== undefined
      );

      if (allAnswered && !completedRef.current) {
        completedRef.current = true;

        const allCorrect = questions.every((q, i) => {
          return (
            normalize(answers[i], q.requireCapitalization) ===
            normalize(q.correctAnswer, q.requireCapitalization)
          );
        });

        onComplete(exercise._id, allCorrect);
      }

      return next;
    });
  };

  /* ================= EDIT MODE RENDER ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            No vocabulary items yet. Click “Add Question” to begin.
          </p>
        )}

        {questions.map((q, qIdx) => (
          <Card key={qIdx} className="p-6 space-y-6">
            <StringField
              label={`Word ${qIdx + 1}`}
              value={q.word}
              onChange={(v) =>
                handleQuestionChange(qIdx, "word", v)
              }
              placeholder="abundant"
              required
              error={
                errors?.[
                  `exercise-${exercise.index}-vb-word-${qIdx}`
                ]
              }
            />

            <div className="space-y-3">
              <label className="font-medium">Options</label>
              {q.options.map((opt, optIdx) => (
                <div key={optIdx} className="flex gap-3 items-end">
                  <StringField
                    value={opt}
                    onChange={(v) =>
                      handleOptionChange(qIdx, optIdx, v)
                    }
                    placeholder="Meaning or synonym"
                  />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() =>
                      handleRemoveOption(qIdx, optIdx)
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleAddOption(qIdx)}
              >
                Add Option
              </Button>
            </div>

            <StringField
              label="Correct Answer"
              value={q.correctAnswer}
              onChange={(v) =>
                handleQuestionChange(qIdx, "correctAnswer", v)
              }
              placeholder="Must match one option"
              required
              error={
                errors?.[
                  `exercise-${exercise.index}-vb-correct-${qIdx}`
                ]
              }
            />

            <CapitalizationRule
              checked={q.requireCapitalization}
              onChange={(c) =>
                handleCapitalizationChange(qIdx, c)
              }
            />

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleRemoveQuestion(qIdx)}
            >
              Remove Question
            </Button>
          </Card>
        ))}

        <Button variant="outline" onClick={handleAddQuestion}>
          Add Question
        </Button>
      </div>
    );
  }

  /* ================= STUDENT VIEW ================= */
  if (questions.length === 0) {
    return (
      <Card className="max-w-2xl mx-auto">
        <CardHeader>
          <CardDescription>
            No vocabulary questions available.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto">
      <CardHeader>
        <CardTitle>Vocabulary Builder</CardTitle>
        <CardDescription>
          Choose the correct meaning or synonym for each word
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, qIdx) => {
          const selected = state.answers[qIdx] || "";

          return (
            <div
              key={qIdx}
              className="p-8 bg-muted rounded-xl space-y-6"
            >
              <div className="text-4xl font-bold text-center text-blue-700">
                {q.word}
              </div>

              <RadioGroup
                value={selected}
                onValueChange={(v) => handleChange(qIdx, v)}
                disabled={showResults}
              >
                <div className="space-y-4">
                  {q.options.map((opt, optIdx) => {
                    const correct =
                      normalize(opt, q.requireCapitalization) ===
                      normalize(q.correctAnswer, q.requireCapitalization);

                    const isSelected = selected === opt;

                    return (
                      <div
                          key={optIdx}
                          className={cn(
                            "flex items-center space-x-4 p-4 rounded-lg border transition",

                            showResults &&
                              isSelected &&
                              correct &&
                              "bg-green-100 border-green-300",

                            showResults &&
                              isSelected &&
                              !correct &&
                              "bg-red-100 border-red-300"
                          )}
                        >
                        <RadioGroupItem
                          value={opt}
                          id={`vocab-${qIdx}-${optIdx}`}
                          className={cn(
                            showResults &&
                              isSelected &&
                              correct &&
                              "border-green-500",
                            showResults &&
                              isSelected &&
                              !correct &&
                              "border-red-500"
                          )}
                        />
                        <Label
                          htmlFor={`vocab-${qIdx}-${optIdx}`}
                          className={cn(
                            "flex-1 cursor-pointer text-lg",
                            showResults &&
                              isSelected &&
                              correct &&
                              "text-green-700 font-semibold",
                            showResults &&
                              isSelected &&
                              !correct &&
                              "text-red-700 font-semibold"
                          )}
                        >
                          {opt}
                          {showResults && isSelected && (
                            <span className="ml-4 text-sm">
                              {correct
                                ? "Correct"
                                : `Wrong → ${q.correctAnswer}`}
                            </span>
                          )}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </RadioGroup>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}
